import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import {
  usePreviewAssignmentFromTeacher,
  useCloneAssignment,
} from "@packages/api/madlenAPI";
import { Button, Text } from "../../../../core";
import { useToast } from "@packages/contexts/ToastContext";
import { useQueryClient } from "@tanstack/react-query";
import { BaseAssignmentResponseDto } from "@packages/api/madlenAPI.schemas";
import CloneAssignmentPreview from "../components/CloneAssignmentPreview";

const CloneAssignmentView = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [isCloning, setIsCloning] = useState(false);

  // Fetch the assignment preview
  const {
    data: assignment,
    isLoading,
    error,
  } = usePreviewAssignmentFromTeacher(Number(id));

  // Clone assignment mutation
  const cloneAssignmentMutation = useCloneAssignment({
    mutation: {
      onMutate: () => {
        setIsCloning(true);
      },
      onSuccess: (newAssignment: BaseAssignmentResponseDto) => {
        addToast({
          title: t("notifications.assignment.cloneSuccess"),
          variant: "success",
          duration: 3000,
        });
        // Redirect to the cloned assignment
        navigate(`/assignments/${newAssignment.id}`);
      },
      onError: (error) => {
        setIsCloning(false);
        addToast({
          title: t("notifications.assignment.cloneError"),
          description: t("notifications.assignment.cloneErrorDescription"),
          variant: "error",
          duration: 3000,
        });
        console.error("Error cloning assignment:", error);
      },
    },
  });

  // Handle clone button click
  const handleClone = () => {
    cloneAssignmentMutation.mutate({ assignmentId: Number(id) });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !assignment) {
    return (
      <div className="flex-1 p-8 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <Text variant="p" className="text-red-500 dark:text-red-400">
              {t("assignments.error")}
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <Button
            onClick={() => navigate("/assignments")}
            size="sm"
            rounded="full"
            bgColor="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
            textColor="text-gray-700 dark:text-gray-300"
            className="mb-4 flex items-center shadow-sm p-2"
          >
            <ArrowLeft size={18} className="mr-1" />
            <span className="text-sm">{t("common.back")}</span>
          </Button>

          {/* Clone Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <Text variant="h1" className="mb-2">
                  {assignment.name}
                </Text>
                <Text variant="p" className="text-gray-600 dark:text-gray-400">
                  {assignment.description || "-"}
                </Text>
              </div>
              <Button
                onClick={handleClone}
                size="md"
                rounded="md"
                bgColor="bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700"
                textColor="text-white"
                className="flex items-center"
                disabled={isCloning}
              >
                {isCloning ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                ) : null}
                <span>{t("assignments.clone")}</span>
              </Button>
            </div>
          </div>

          {/* Preview */}
          <CloneAssignmentPreview assignment={assignment} />
        </div>
      </div>
    </div>
  );
};

export default CloneAssignmentView;
